import React from 'react';

import CoverSheet from '../general/coverSheet';
import CurrentTestingReport from './currentTestingReport';
import ETAnomalyPDF from './ETAnomalyPDF';
import ETSummarySheet from './etSummarySheet';
import PDFInfoSheet from '../../../themed/pdf/pdfInfoSheet';
import PDFPage from '../../../themed/pdf/pdfPage';
import PDFPhotoPage from '../../../themed/pdf/pdfPhotoPage';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import withPDF from '../../../hoc/withPDF';

import * as dummyData from '../../../../utils/data';
import * as models from '../../../../utils/models';
import * as utils from '../../../../utils/utilities';

interface ETReportProps {
	projectDetails: models.Project;
	coverSheetData: models.ETCoverData;
	summarySheetData: models.ETSummarySheetData;
	etWallThicknessData: models.ETWallThicknessItem[];
	currentTestingData: models.ETIReportItem[];
	anomalyData: models.VTEquipmentAnomalyData[];
	drawingImages: models.PDFImageProps[];
	eciOverview: models.ETIReportOverview;
}

const ETReport = (props: ETReportProps): JSX.Element => {
	const { etWallThicknessData, projectDetails } = props;

	const standardCriteriaItems: models.CoverDataItem[] = [
		{
			label: '1. Qualification of NDT Personnel',
			value: props.coverSheetData?.ndtQualification,
		},

		{
			label: '2. Non Destructive Testing',
			value: props.coverSheetData?.nonDestructiveTesting,
		},
		{
			label:
				'3.  Damage Mechanisms Affecting Fixed Equipment in the Refining Industry',
			value: props.coverSheetData?.damageMechanisms,
		},
	];

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverSheetData?.scopeOfWork,
		},
		{
			label: '2. Visual Examination Procedure',
			value: props.coverSheetData?.veProcedure,
		},
		{
			label: '3. Eddy Current Examination Procedure',
			value: props.coverSheetData?.eceProcedure,
		},
	];

	const pageSubtitle: string =
		'Document Number: ' + projectDetails.documentNumber;

	const renderThicknessData = () => {
		const chunkedThicknessData = utils.getChunkedData(etWallThicknessData, 15);
		return chunkedThicknessData.map((data, index) => (
			<PDFPage
				key={index.toString()}
				projectDetails={projectDetails}
				pageDetails={utils.getPageDetails(
					'Diamensional & Wall Thickness Page',
					'Structural Beam Details'
				)}
				enableTitlePage={index === 0}
			>
				<PDFTablePage
					data={data}
					columns={dummyData.getETWallThicknessTableColumns()}
				/>
			</PDFPage>
		));
	};

	const renderETData = () => {
		const chunkedETData = utils.getChunkedData(props.currentTestingData, 15);
		return chunkedETData.map((data, index: number) => (
			<PDFPage
				key={index.toString()}
				projectDetails={projectDetails}
				pageDetails={utils.getPageDetails('Eddy Current Testing Report')}
				enableTitlePage={index === 0}
			>
				<CurrentTestingReport
					projectDetails={projectDetails}
					data={data}
					{...props.eciOverview}
				/>
			</PDFPage>
		));
	};

	return (
		<React.Fragment>
			<PDFInfoSheet
				companyUrl={projectDetails.organisation.organisationLogo}
				documentNo={projectDetails.documentNumber}
				clientName={projectDetails.organisationName}
				facility={projectDetails.facilityName}
				reportTitle={projectDetails.projectTitle}
			/>

			<PDFPage
				pageDetails={utils.getPageDetails('Cover Sheet', pageSubtitle)}
				projectDetails={projectDetails}
			>
				<CoverSheet
					overviewData={overviewData}
					referenceDocuments={referenceDocuments}
					standardCriteriaItems={standardCriteriaItems}
				/>
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Summary Sheet')}
				projectDetails={projectDetails}
			>
				<ETSummarySheet {...props.summarySheetData} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Drawing Page')}
				projectDetails={projectDetails}
			>
				<PDFPhotoPage photos={props.drawingImages} />
			</PDFPage>

			{renderETData()}

			{renderThicknessData()}

			<ETAnomalyPDF data={props.anomalyData} project={projectDetails} />
		</React.Fragment>
	);
};

const overviewData: models.CoverDataOverview[] = [
	{ report: '1. Cover Sheet', required: 'Yes', page: '2' },
	{ report: '2. Summary Sheet', required: 'Yes', page: '4' },
	{
		report: '3. Drawing Page',
		required: 'Yes',
		page: '6',
	},
	{
		report: '4. Diamensional & Wall Thickness Page',
		required: 'Yes',
		page: '8',
	},
	{
		report: '5. Eddy current Inspection Report (ET)',
		required: 'Yes',
		page: '8',
	},
	{
		report: '6. Anomaly Report',
		required: 'Yes',
		page: '10',
	},
	{
		report: '7. Photographic Report',
		required: 'Yes',
		page: '12',
	},
];

export default withPDF(ETReport);
